import { Box, InputAdornment, Select as MUISelect, TextField } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { SelectChangeEvent } from "@mui/material/Select";
import DropDownIcon from "assets/DropDownIcon";
import { ReactComponent as SearchIcon } from "assets/searchicon.svg";
import { fetchAdminInncodesAsync } from "components/features/MyPlans/myplans.slice";
import { isFunction, uniqBy } from "lodash";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { DEFAULT_CORP_INNCODE } from "utils/CommonConstants";
import { getHltInncodes, useDebouncedEffect } from "utils/CommonFunctions";
import Bookmarkinncode from "../BookmarkInncode";
import Typography from "../Typography";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

interface Props {
  label?: string;
  options?: any;
  startAdornment?: any;
  disabled?: boolean;
  iconComponent?: any;
  backgroundColor?: string;
  color?: string;
  width?: string;
  height?: string;
  onChange?: any;
  value?: any;
  isAdmin?: boolean;
  inputValue?: string;
  externalOptions?: string[];
  hideSearch?: boolean;
}
const CORP_INNCODE = [{ id: "NA", innCode: DEFAULT_CORP_INNCODE, name: "CORP Inncode", isBookmarked: true }];

export default function InncodeDropDown(props: Props) {
  const {
    label,
    options,
    startAdornment,
    disabled,
    backgroundColor = tokens.Background.General.Tertiary,
    color,
    width,
    value,
    onChange,
    isAdmin,
    externalOptions,
    hideSearch = false
  } = props;
  const [selectedOption, setSelectedOption] = useState(value ? [value] : [options?.[0]]);
  const adminInnCodes: any = useAppSelector((state) => state.myplans.MyPlans.adminInncodes);
  const bookMarkInncodes = useAppSelector((state) => state.myplans.MyPlans.bookMarkInncodes);
  const innCode = useAppSelector((state: any) => state.app.innCode);
  const innCodes = getHltInncodes(true);
  const [inputValue, setInputValue] = useState("");
  const innCodeDetails = useAppSelector((state: any) => state.myplans.MyPlans.innCodeDetails);
  const [adminInncodeOption, setAdminInncodeOptions] = useState<any>(CORP_INNCODE);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isAdmin) {
      const selectedInncodeDetails = adminInnCodes.find((item: any) => item.innCode === innCode);
      const defaultInncodeList = selectedInncodeDetails
        ? [...CORP_INNCODE, ...bookMarkInncodes, selectedInncodeDetails, ...innCodeDetails]
        : [...CORP_INNCODE, ...bookMarkInncodes, ...innCodeDetails];

      const list = !!inputValue ? adminInnCodes : defaultInncodeList;
      let finalArr = uniqBy(list, "innCode").map((item: any) => {
        return {
          ...item,
          isBookmarked: !![...CORP_INNCODE, ...bookMarkInncodes].find((currVal: any) => {
            return currVal.innCode === item.innCode;
          })
        };
      });
      setAdminInncodeOptions(finalArr);
    }
  }, [adminInnCodes, bookMarkInncodes, inputValue]);

  useEffect(() => {
    if (externalOptions?.length) {
      setAdminInncodeOptions(externalOptions);
    }
  }, [externalOptions]);

  useDebouncedEffect(
    () => {
      if (inputValue && inputValue.length > 2) dispatch(fetchAdminInncodesAsync(inputValue));
    },
    [inputValue],
    500
  );
  useEffect(() => {
    setSelectedOption([value]);
  }, [value, options]);

  const stopImmediatePropagation = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleChange = (event: SelectChangeEvent<any>) => {
    const {
      target: { value }
    } = event;
    setSelectedOption(value);
    if (isFunction(onChange)) onChange(value);
  };
  const handleSelectClose = () => {
    setInputValue("");
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "150px",
        height: "44px",
        borderRadius: "8px",
        gap: "8px"
      }}
    >
      {label ? <Typography variant="caption">{label}</Typography> : <></>}
      <MUISelect
        sx={{
          height: "44px",
          padding: "0px 8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: { backgroundColor },
          color: color,
          fontWeight: 700,
          fontSize: "14px",
          lineHeight: "21px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          boxShadow: "none",
          ".MuiOutlinedInput-notchedOutline": { border: 0 },
          " .MuiSelect-icon": { alignSelf: "center", top: "20%" }
        }}
        onClose={handleSelectClose}
        IconComponent={DropDownIcon}
        disabled={disabled}
        value={selectedOption}
        onChange={handleChange}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
              width: width
            }
          }
        }}
        inputProps={{ "aria-label": "Without label" }}
        renderValue={(selected) => selected}
        startAdornment={
          startAdornment ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginRight: "10px"
              }}
            >
              <img height={"25px"} alt="UserPicture" src={startAdornment} />
            </Box>
          ) : (
            <></>
          )
        }
      >
        {isAdmin && !hideSearch && (
          <MenuItem
            sx={{
              padding: "0"
            }}
            divider
            value={""}
            key="inputItem"
            onClickCapture={stopImmediatePropagation}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <TextField
              inputRef={(input) => {
                if (input != null) {
                  setTimeout(() => {
                    input.focus();
                  });
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{
                width: "100%",
                padding: "10px",
                ".MuiInputBase-input.MuiOutlinedInput-input": {
                  padding: "10px"
                }
              }}
              value={inputValue}
              onChange={(e: any) => setInputValue(e.target.value)}
            />
          </MenuItem>
        )}
        {isAdmin &&
          adminInncodeOption?.map((option: any, index: any) => (
            <MenuItem key={option.innCode} value={option.innCode} disabled={option?.isDisabled}>
              {option?.name ? (
                <>
                  <Typography sx={{ marginX: "8px", whiteSpace: "normal" }} variant={"body2"}>
                    {isAdmin ? `${option?.name} (${option.innCode})` : option.name}
                  </Typography>
                  {isAdmin && !externalOptions?.length && (index === 0 ? !!inputValue : true) && (
                    <Bookmarkinncode isBookmarked={option.isBookmarked} item={option} />
                  )}
                </>
              ) : (
                option?.innCode
              )}
            </MenuItem>
          ))}
        {!isAdmin &&
          innCodes
            ?.split(",")
            .sort()
            .map((option: any) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
      </MUISelect>
    </Box>
  );
}
